import * as _ from "lodash-es";
import { notifyError } from "@/notify";
import store from "@/store";
import tokenStorage from "@/tokenStorage";

const SCHOOL_WS_ACTIONS = {
  SMS_LIST_BY_PHONE: "sms.list_by_phone",
  SMS_PROCESSED: "sms.processed",
  SMS_GET_SEGMENTS: "sms.get_segments",
  SMS_GET_MOBILE_PHONE_SETTINGS: "sms.get_mobile_phone_settings",
  SMS_UPDATE_MOBILE_PHONE_SETTINGS: "sms.update_mobile_phone_settings",
  AUTH_TOKEN: "auth.token",
  AUTH_OK: "auth.ok",
  NOTIFICATION_LIST_UNREAD: "notification.list_unread",
  NOTIFICATION_READ: "notification.read",
  NOTIFICATION_NEW: "notification.new",
  RECORD_EDIT: "record.edit",
  RECORD_LEFT: "record.left",
  RECORD_EDIT_LIST: "record.edit_list",
};

// 3, 6 and 12 seconds
const MAX_RETRIES = 3;
const BACKOFF_FACTOR = 3;

export default {
  /** @type {(WebSocket|null)} */
  ws: null,
  /**
   * Number of failed connection attempts.
   * Required to limit connecting retries number.
   *
   * E.g.
   * Staff logs in, ws is open.
   * Staff goes away from pc, pc goes into sleep mode.
   * Staff comes back, websocket connection closes and tries to reopen, but staff token is expired.
   *
   * @type {int}
   */
  failedAttempts: 0,

  initWebSocket() {
    const token = tokenStorage.getAccessToken();
    const isTest = Boolean(window.SchoolSettings && window.SchoolSettings.test_details);
    if (!token || this.ws || isTest) {
      return;
    }
    if (!store || !store.getters.isAuthenticated) {
      // Wait for user data to be fetched to know what is the user's group
      this.reopen();
      return;
    }
    if (!store.getters.isStaff) {
      return;
    }

    this.close();
    this.ws = new WebSocket(
      `${window.location.protocol.replace("http", "ws")}//${window.location.host}/ws/`,
    );

    this.ws.onopen = () => {
      this.authenticate(token);
    };

    this.ws.onclose = () => {
      this.ws = null;
      store.commit("webSocket/setAuthenticated", false);
      this.reopen();
    };

    this.ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.errors) {
        const errors = _.flatten(Object.values(data.errors));
        notifyError(errors.join(" "));
        return;
      } else if (data.action === SCHOOL_WS_ACTIONS.AUTH_OK) {
        // The websocket authentication passed, make initial requests here.
        store.commit("webSocket/setAuthenticated", true);
        this.clearFailedAttempts();
        this.fetchSMSSegments();
        this.fetchUnreadNotifications();
      } else if (data.action === SCHOOL_WS_ACTIONS.SMS_LIST_BY_PHONE) {
        store.commit("sms/addSMSMessages", data.payload);
      } else if (data.action === SCHOOL_WS_ACTIONS.SMS_PROCESSED) {
        store.commit("sms/addSMSMessages", data.payload);
        store.commit("sms/recalculateSMSSegments", data.payload);
      } else if (data.action === SCHOOL_WS_ACTIONS.SMS_GET_SEGMENTS) {
        store.commit("sms/setSMSSegments", data.payload);
      } else if (
        [
          SCHOOL_WS_ACTIONS.SMS_GET_MOBILE_PHONE_SETTINGS,
          SCHOOL_WS_ACTIONS.SMS_UPDATE_MOBILE_PHONE_SETTINGS,
        ].includes(data.action)
      ) {
        store.commit("sms/updatePhoneNumberSMSSettings", data.payload);
      } else if (data.action === SCHOOL_WS_ACTIONS.NOTIFICATION_LIST_UNREAD) {
        store.commit("notification/setNotificationsLoading", false);
        // TODO: page size param.
        store.commit("notification/setAllNotificationsLoaded", data.payload.length < 25);
        store.commit("notification/addNotifications", data.payload);
      } else if (
        [
          SCHOOL_WS_ACTIONS.NOTIFICATION_NEW,
          SCHOOL_WS_ACTIONS.NOTIFICATION_READ,
        ].includes(data.action)
      ) {
        store.commit("notification/addNotifications", data.payload);
      } else if (data.action === SCHOOL_WS_ACTIONS.RECORD_EDIT_LIST) {
        store.commit("dashboard/setRecordEditing", data.payload);
      }
    };
  },

  clearFailedAttempts() {
    this.failedAttempts = 0;
  },

  retry(timeout) {
    setTimeout(() => {
      this.initWebSocket();
    }, timeout);
  },

  reopen() {
    this.failedAttempts++;
    if (this.failedAttempts > MAX_RETRIES) {
      this.clearFailedAttempts();
      return;
    }
    this.retry(1000 * BACKOFF_FACTOR * Math.pow(2, this.failedAttempts - 1));
  },

  close() {
    if (this.ws && [this.ws.OPEN, this.ws.CONNECTING].includes(this.ws.readyState)) {
      this.ws.close();
    }
    this.ws = null;
  },

  sendData(action, payload = {}) {
    if (
      !this.ws ||
      [this.ws.CLOSED, this.ws.CLOSING, this.ws.CONNECTING].includes(this.ws.readyState)
    ) {
      return;
    }
    this.ws.send(JSON.stringify({ action, payload }));
  },

  fetchSMSByPhone(phone) {
    this.sendData(SCHOOL_WS_ACTIONS.SMS_LIST_BY_PHONE, {
      mobile_phone: phone,
    });
  },

  fetchPhoneNumberSMSSettings(phone) {
    if (store.state.sms.smsSettingsByPhone[phone]) {
      return;
    }
    this.sendData(SCHOOL_WS_ACTIONS.SMS_GET_MOBILE_PHONE_SETTINGS, {
      mobile_phone: phone,
    });
  },

  updatePhoneNumberSMSSettings(data) {
    this.sendData(SCHOOL_WS_ACTIONS.SMS_UPDATE_MOBILE_PHONE_SETTINGS, data);
  },

  authenticate(token) {
    this.sendData(SCHOOL_WS_ACTIONS.AUTH_TOKEN, {
      token: token,
    });
  },

  fetchSMSSegments() {
    if (store.state.staffStore.is_business_sms_supported) {
      this.sendData(SCHOOL_WS_ACTIONS.SMS_GET_SEGMENTS);
    }
  },

  fetchUnreadNotifications(params = {}) {
    if (store.state.notification.isNotificationsLoading) {
      return;
    }
    store.commit("notification/setNotificationsLoading", true);
    this.sendData(SCHOOL_WS_ACTIONS.NOTIFICATION_LIST_UNREAD, params);
  },

  markNotificationAsRead(id) {
    this.sendData(SCHOOL_WS_ACTIONS.NOTIFICATION_READ, { id: id });
  },

  editRecord(recordId) {
    this.sendData(SCHOOL_WS_ACTIONS.RECORD_EDIT, { record_id: recordId });
  },

  leaveRecord(recordId) {
    this.sendData(SCHOOL_WS_ACTIONS.RECORD_LEFT, { record_id: recordId });
  },
};
